import { CUSTOMER_SYSTEM_FIELDS as USER_SYSTEM_FIELDS, type CustomField } from '@cheqroom/api';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import type { FilterFieldEntry } from './types';
import { getDynamicFilterEntries } from './utils/get-dynamic-filter-entries';

const getUserStatusOptions = (t: TFunction<'contact-filters'>) => [
	{ label: t('contact-filters:status.options.active'), value: 'ACTIVE' },
	{ label: t('contact-filters:status.options.blocked'), value: 'BLOCKED' },
	{ label: t('contact-filters:status.options.pending_invite'), value: 'PENDING_INVITE' },
	{ label: t('contact-filters:status.options.expired_invite'), value: 'EXPIRED_INVITE' },
	{ label: t('contact-filters:status.options.archived'), value: 'ARCHIVED' },
];

const getMaintenanceOptions = (t: TFunction<'contact-filters'>) => [
	{ label: t('contact-filters:maintenance.options.all'), value: 'all' },
	{ label: t('contact-filters:maintenance.options.include'), value: 'maintenance' },
	{ label: t('contact-filters:maintenance.options.exclude'), value: 'no_maintenance' },
];

const getSeatTypeOptions = (t: TFunction<'contact-filters'>) => [
	{ label: t('contact-filters:seat_type.options.admin'), value: 'admin' },
	{ label: t('contact-filters:seat_type.options.operator'), value: 'operator' },
];

const getAccessOptions = (t: TFunction<'contact-filters'>) => [
	{ label: t('contact-filters:access.options.all'), value: 'all' },
	{ label: t('contact-filters:access.options.full_access'), value: 'full_access' },
	{ label: t('contact-filters:access.options.booking_portal_access'), value: 'booking_portal_access' },
	{ label: t('contact-filters:access.options.workspace_access'), value: 'workspace_access' },
	{ label: t('contact-filters:access.options.no_access'), value: 'no_access' },
];

interface UseUserFiltersProps {
	t: TFunction<'contact-filters'>;
	customFields?: CustomField[];
	allowBookingPortal?: boolean;
}

export const useUserFilters = ({ t, customFields, allowBookingPortal = false }: UseUserFiltersProps) => {
	const filters = useMemo<FilterFieldEntry[]>(() => {
		const staticFilters: FilterFieldEntry[] = [
			{ id: 'status', name: t('filter.status.trigger'), type: 'select', options: getUserStatusOptions(t) },
			{
				id: 'maintenance',
				name: t('contact-filters:maintenance.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getMaintenanceOptions(t),
			},
			{
				id: 'seatType',
				name: t('contact-filters:seat_type.trigger'),
				type: 'select',
				options: getSeatTypeOptions(t),
			},
			{
				id: 'access',
				name: t('contact-filters:access.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getAccessOptions(t).filter(
					(option) => allowBookingPortal || !['booking_portal_access', 'full_access'].includes(option.value)
				),
			},
			{ id: 'role', name: t('contact-filters:role.trigger') },
		];

		const dynamicFilters = getDynamicFilterEntries([
			...USER_SYSTEM_FIELDS,
			...(customFields ?? []).map((field) => ({ ...field, id: `fields.${field.name}` })),
		]);

		return [...staticFilters, ...dynamicFilters];
	}, [t, customFields, allowBookingPortal]);

	return {
		filters,
	};
};
